<template>
  <DefaultLayout>
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Gestión de Agentes</h1>
        <p class="subtitle">Crea, edita y administra los agentes de IA de la plataforma</p>
      </div>
      <Button
        label="Nuevo agente"
        icon="pi pi-plus"
        @click="openCreate"
      />
    </div>

    <!-- Filtros -->
    <div class="filters-bar">
      <span class="p-input-icon-left search-wrap">
        <i class="pi pi-search" />
        <InputText
          v-model="search"
          placeholder="Buscar por nombre o categoría..."
          class="search-input"
        />
      </span>
      <Dropdown
        v-model="filterCategory"
        :options="categoryOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Todas las categorías"
        showClear
        class="filter-dropdown"
      />
      <Dropdown
        v-model="filterStatus"
        :options="[{label:'Todos',value:null},{label:'Activos',value:true},{label:'Inactivos',value:false}]"
        optionLabel="label"
        optionValue="value"
        placeholder="Estado"
        class="filter-dropdown"
      />
    </div>

    <!-- Tabla de agentes -->
    <Card>
      <template #content>
        <DataTable
          :value="filteredAgents"
          :loading="loading"
          responsiveLayout="scroll"
          :rows="10"
          paginator
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          :rowsPerPageOptions="[5, 10, 20]"
          stripedRows
          class="agents-table"
        >
          <!-- Nombre + categoría -->
          <Column field="name" header="Agente" sortable style="min-width: 200px">
            <template #body="{ data }">
              <div class="agent-cell">
                <div :class="['agent-avatar', data.category]">
                  <i :class="['pi', categoryIcon(data.category)]" />
                </div>
                <div>
                  <div class="agent-name">{{ data.name }}</div>
                  <div class="agent-category">{{ categoryLabel(data.category) }}</div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Descripción -->
          <Column field="description" header="Descripción" style="min-width: 250px">
            <template #body="{ data }">
              <span class="description-cell">
                {{ data.description || '—' }}
              </span>
            </template>
          </Column>

          <!-- Proveedor -->
          <Column header="Proveedor" style="min-width: 130px">
            <template #body="{ data }">
              <Tag
                v-if="data.ai_provider_id"
                :value="providerName(data.ai_provider_id)"
                severity="info"
              />
              <span v-else class="text-muted">Sistema</span>
            </template>
          </Column>

          <!-- Estado -->
          <Column field="is_active" header="Estado" sortable style="min-width: 100px">
            <template #body="{ data }">
              <Tag
                :value="data.is_active ? 'Activo' : 'Inactivo'"
                :severity="data.is_active ? 'success' : 'secondary'"
              />
            </template>
          </Column>

          <!-- Fecha -->
          <Column field="created_at" header="Creado" sortable style="min-width: 120px">
            <template #body="{ data }">
              {{ formatDate(data.created_at) }}
            </template>
          </Column>

          <!-- Acciones -->
          <Column header="Acciones" style="min-width: 160px; text-align: right">
            <template #body="{ data }">
              <div class="action-buttons">
                <Button
                  icon="pi pi-comments"
                  text
                  rounded
                  severity="info"
                  v-tooltip.top="'Chatear'"
                  @click="goToChat(data.id)"
                />
                <Button
                  icon="pi pi-pencil"
                  text
                  rounded
                  severity="secondary"
                  v-tooltip.top="'Editar'"
                  @click="openEdit(data)"
                />
                <Button
                  :icon="data.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
                  text
                  rounded
                  :severity="data.is_active ? 'warning' : 'success'"
                  :v-tooltip.top="data.is_active ? 'Desactivar' : 'Activar'"
                  @click="toggleStatus(data)"
                />
                <Button
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  v-tooltip.top="'Eliminar'"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>

          <template #empty>
            <div class="empty-table">
              <i class="pi pi-android" style="font-size:2rem;color:#d1d5db" />
              <p>No hay agentes que coincidan con los filtros.</p>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Dialog crear/editar -->
    <Dialog
      v-model:visible="showForm"
      :header="editingAgent ? `Editar: ${editingAgent.name}` : 'Nuevo agente'"
      modal
      :style="{ width: '760px' }"
      :breakpoints="{ '900px': '95vw' }"
      :maximizable="true"
      @hide="closeForm"
    >
      <TabView>
        <TabPanel header="📝 Configuración">
          <AgentForm
            :agent="editingAgent"
            @saved="onSaved"
            @cancel="closeForm"
          />
        </TabPanel>
        <TabPanel :header="`🎯 Fine-tuning${editingAgent ? '' : ' (guarda primero)'}`" :disabled="!editingAgent">
          <PromptTuning v-if="editingAgent" :agent-id="editingAgent.id" />
          <div v-else class="tab-disabled-msg">Guarda el agente primero para agregar ejemplos de fine-tuning.</div>
        </TabPanel>
      </TabView>
    </Dialog>

    <!-- Dialog confirmar eliminación -->
    <Dialog
      v-model:visible="showDeleteConfirm"
      header="Confirmar eliminación"
      modal
      :style="{ width: '420px' }"
    >
      <div class="delete-confirm">
        <i class="pi pi-exclamation-triangle warn-icon" />
        <div>
          <p>¿Estás seguro de que deseas eliminar el agente</p>
          <p><strong>{{ deletingAgent?.name }}</strong>?</p>
          <p class="delete-warning">
            Esta acción desactivará el agente. El historial de conversaciones se conservará.
          </p>
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showDeleteConfirm = false" />
        <Button
          label="Eliminar"
          severity="danger"
          icon="pi pi-trash"
          :loading="deleting"
          @click="executeDelete"
        />
      </template>
    </Dialog>

    <!-- Toast -->
    <Toast />
  </DefaultLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import DefaultLayout from '../../components/layout/DefaultLayout.vue';
import AgentForm from '../../components/agents/AgentForm.vue';
import PromptTuning from '../../components/agents/PromptTuning.vue';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Toast from 'primevue/toast';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import { agentsService } from '../../services/agents';

const router = useRouter();
const toast = useToast();

// Estado
const agents = ref([]);
const loading = ref(false);
const search = ref('');
const filterCategory = ref(null);
const filterStatus = ref(null);
const showForm = ref(false);
const editingAgent = ref(null);
const showDeleteConfirm = ref(false);
const deletingAgent = ref(null);
const deleting = ref(false);

// Opciones de categoría para el filtro
const categoryOptions = [
  { label: 'General', value: 'general' },
  { label: 'Financiero', value: 'financiero' },
  { label: 'Soporte', value: 'soporte' },
  { label: 'Ventas', value: 'ventas' },
  { label: 'Legal', value: 'legal' },
  { label: 'RRHH', value: 'rrhh' },
  { label: 'Logística', value: 'logistica' },
  { label: 'Salud', value: 'salud' },
  { label: 'Educación', value: 'educacion' },
];

const providerMap = { 1: 'Mistral AI', 2: 'OpenAI', 3: 'Google STT', 4: 'Google TTS', 5: 'Azure', 6: 'ElevenLabs' };

// Filtrado reactivo
const filteredAgents = computed(() => {
  let list = agents.value;
  if (search.value.trim()) {
    const q = search.value.toLowerCase();
    list = list.filter(a =>
      a.name.toLowerCase().includes(q) ||
      (a.category || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q)
    );
  }
  if (filterCategory.value) {
    list = list.filter(a => a.category === filterCategory.value);
  }
  if (filterStatus.value !== null) {
    list = list.filter(a => a.is_active === filterStatus.value);
  }
  return list;
});

// Helpers
const categoryIcon = (cat) => ({
  financiero: 'pi-chart-line', soporte: 'pi-headphones',
  ventas: 'pi-shopping-cart', legal: 'pi-book',
  rrhh: 'pi-users', logistica: 'pi-truck',
  salud: 'pi-heart', educacion: 'pi-graduation-cap',
  general: 'pi-android',
}[cat] || 'pi-android');

const categoryLabel = (cat) => ({
  financiero: 'Financiero', soporte: 'Soporte', ventas: 'Ventas',
  legal: 'Legal', rrhh: 'RRHH', logistica: 'Logística',
  salud: 'Salud', educacion: 'Educación', general: 'General',
}[cat] || cat);

const providerName = (id) => providerMap[id] || `Proveedor ${id}`;

const formatDate = (d) => d
  ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

// Acciones
const loadAgents = async () => {
  loading.value = true;
  try {
    agents.value = await agentsService.getAgents();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los agentes', life: 3000 });
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingAgent.value = null;
  showForm.value = true;
};

const openEdit = (agent) => {
  editingAgent.value = { ...agent };
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  editingAgent.value = null;
};

const onSaved = async () => {
  closeForm();
  await loadAgents();
  toast.add({
    severity: 'success',
    summary: 'Guardado',
    detail: editingAgent.value ? 'Agente actualizado correctamente' : 'Agente creado correctamente',
    life: 3000,
  });
};

const toggleStatus = async (agent) => {
  try {
    await agentsService.updateAgent(agent.id, { is_active: !agent.is_active });
    agent.is_active = !agent.is_active;
    toast.add({
      severity: 'success',
      summary: agent.is_active ? 'Activado' : 'Desactivado',
      detail: `Agente "${agent.name}" ${agent.is_active ? 'activado' : 'desactivado'}`,
      life: 2500,
    });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cambiar el estado', life: 3000 });
  }
};

const confirmDelete = (agent) => {
  deletingAgent.value = agent;
  showDeleteConfirm.value = true;
};

const executeDelete = async () => {
  if (!deletingAgent.value) return;
  deleting.value = true;
  try {
    await agentsService.deleteAgent(deletingAgent.value.id);
    showDeleteConfirm.value = false;
    await loadAgents();
    toast.add({
      severity: 'success',
      summary: 'Eliminado',
      detail: `Agente "${deletingAgent.value.name}" eliminado`,
      life: 3000,
    });
    deletingAgent.value = null;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el agente', life: 3000 });
  } finally {
    deleting.value = false;
  }
};

const goToChat = (agentId) => router.push(`/chat/${agentId}`);

onMounted(loadAgents);
</script>

<style scoped>
/* Header */
.page-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem;
}
.page-header h1 { margin: 0; color: #1e293b; font-size: 1.5rem; }
.subtitle { color: #6b7280; margin: 0.25rem 0 0; font-size: 0.875rem; }

/* Filtros */
.filters-bar {
  display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;
}
.search-wrap { flex: 1; min-width: 200px; }
.search-input { width: 100%; }
.filter-dropdown { min-width: 180px; }

/* Tabla */
.agent-cell { display: flex; align-items: center; gap: 0.75rem; }
.agent-avatar {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 1rem;
}
.agent-avatar.financiero { background: #dbeafe; color: #1d4ed8; }
.agent-avatar.soporte    { background: #dcfce7; color: #15803d; }
.agent-avatar.ventas     { background: #fef3c7; color: #b45309; }
.agent-avatar.legal      { background: #ede9fe; color: #6d28d9; }
.agent-avatar.rrhh       { background: #fce7f3; color: #be185d; }
.agent-avatar.logistica  { background: #ffedd5; color: #c2410c; }
.agent-avatar.salud      { background: #fee2e2; color: #b91c1c; }
.agent-avatar.educacion  { background: #e0f2fe; color: #0369a1; }
.agent-avatar.general    { background: #f3e8ff; color: #7c3aed; }

.agent-name { font-weight: 600; color: #1e293b; font-size: 0.9rem; }
.agent-category { font-size: 0.75rem; color: #6b7280; }
.description-cell { font-size: 0.85rem; color: #4b5563; }
.text-muted { color: #9ca3af; font-size: 0.85rem; }

.action-buttons { display: flex; justify-content: flex-end; gap: 0.25rem; }

/* Empty state */
.empty-table { text-align: center; padding: 2rem; color: #9ca3af; }
.empty-table p { margin-top: 0.5rem; }

/* Delete confirm */
.delete-confirm { display: flex; align-items: flex-start; gap: 1rem; padding: 0.5rem 0; }
.warn-icon { font-size: 2.5rem; color: #f59e0b; flex-shrink: 0; }
.delete-confirm p { margin: 0.2rem 0; color: #374151; }
.delete-warning { font-size: 0.8rem; color: #6b7280; margin-top: 0.5rem !important; }
</style>
